import {Fragment, type FC, useMemo, useState} from "react"
import {ReactSVG} from "react-svg"
import {decodeToken} from "react-jwt"
import {useNavigate} from "react-router-dom"
import {Trans, useTranslation} from "react-i18next"
import {useQuery} from "@tanstack/react-query"
import {Avatar, Box, Skeleton, Stack, Typography} from "@mui/material"
import {Button, NewButton} from "../../atoms"
import {stringToAvatar} from "../../../helpers"
import FindExpert from "../FindExpert/FindExpert"
import {TextWithTooltip} from "../../molecules"
import {useConfigProvider} from "../../../config"
import {accountEndpoints} from "../../../services"
import ContactModal from "../ContactModal/ContactModal"
import useExpertBioStore from "../../../stores/expertBioStore"
import type {Account, AccountTeam, Token} from "../../../types"
import useStorage, {StorageData} from "../../../hooks/useStorage"
import {CrmAccount} from "../../../types/CrmAccount"

type AccountCardProps = {
  name: string
  id?: string
  loading?: boolean
  onClick?: () => void
}
const AccountCard: FC<AccountCardProps> = (props) => {
  const {name, loading, id, onClick} = props
  const {getProfilePicture, getJobTitle} = accountEndpoints()
  const {t, i18n} = useTranslation()
  const getProfilePictureQuery = useQuery({
    queryKey: ["profilePicture", id],
    queryFn: () => getProfilePicture(id!!),
    enabled: !!id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const getJobTitleQuery = useQuery<{jobTitle: string}>({
    queryKey: ["jobTitle", id],
    queryFn: () => getJobTitle(id!!),
    enabled: !!id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const profilePicture = useMemo(() => {
    if (getProfilePictureQuery.data) {
      return URL.createObjectURL(getProfilePictureQuery.data)
    }
    return ""
  }, [getProfilePictureQuery.data])

  const jobTitle = getJobTitleQuery.data?.jobTitle

  return (
    <Stack
      component="li"
      height="46px"
      width="100%"
      direction="row"
      gap="0.5rem"
      padding="0.25rem"
      borderRadius="0.5rem"
      sx={{
        cursor: loading ? "default" : "pointer",
      }}
      onClick={loading ? undefined : onClick}
    >
      {loading ||
      getProfilePictureQuery.isLoading ||
      getProfilePictureQuery.isFetching ? (
        <Skeleton animation="wave" variant="circular" width={36} height={36} />
      ) : (
        <Avatar
          {...stringToAvatar(name, {
            width: "36px",
            height: "36px",
            fontSize: "0.875rem",
          })}
          src={profilePicture || undefined}
          alt={name}
          slotProps={{
            img: {width: "36px", height: "36px", loading: "lazy"},
          }}
        />
      )}
      <Stack component="div" direction="column" flexGrow={1}>
        {loading ? (
          <Skeleton animation="wave" variant="text" width="75%" height={20} />
        ) : (
          <>
            <TextWithTooltip
              limit={i18n.languages[0] === "en" ? 18 : 12}
              text={name}
              includeEllipsis={false}
              tooltipStyle={{
                "& .MuiTooltip-tooltip": {
                  mt: "3px !important",
                  textAlign: "left",
                  borderRadius: 0,
                  border: "1px solid #1B1B1B",
                  bgcolor: "#FFF",
                  color: "#242D35",
                  p: "2px 8px",
                },
              }}
              textStyle={{
                fontSize: "0.875rem",
                fontWeight: "normal",
                fontStyle: "normal",
                lineHeight: "1.43",
                letterSpacing: "normal",
                textAlign: "left",
                color: "#1c1c1c",
              }}
            />
          </>
        )}
        {loading ||
        getJobTitleQuery.isLoading ||
        getJobTitleQuery.isFetching ? (
          <Skeleton animation="wave" variant="text" width="40%" height={16} />
        ) : (
          <Box>
            <TextWithTooltip
              limit={i18n.languages[0] === "en" ? 24 : 12}
              text={jobTitle ? t(jobTitle) : ""}
              includeEllipsis={false}
              tooltipStyle={{
                "& .MuiTooltip-tooltip": {
                  mt: "3px !important",
                  textAlign: "left",
                  borderRadius: 0,
                  border: "1px solid #1B1B1B",
                  bgcolor: "#FFF",
                  color: "#242D35",
                  p: "2px 8px",
                },
              }}
              textStyle={{
                fontSize: "0.75rem",
                fontWeight: "normal",
                fontStyle: "normal",
                lineHeight: "1.33",
                letterSpacing: "normal",
                textAlign: "left",
                color: "#1c1c1c66",
                whiteSpace: "nowrap",
              }}
            />
          </Box>
        )}
      </Stack>
    </Stack>
  )
}

const headerHeight = 88
const containerHeight = window.innerHeight - headerHeight
const containerYPadding = 40
const accountTeamMaxHeight = (containerHeight - containerYPadding) * 0.6
const cardHeight = 46
const cardGap = 8
// Calculate the number of cards that can fit in the container before view more button is shown
const cardCount = Math.floor(accountTeamMaxHeight / (cardHeight + cardGap))

const AccountPanel: FC = () => {
  const {setShowFindExpert} = useExpertBioStore()
  const navigate = useNavigate()
  const {t} = useTranslation()
  const {token} = useConfigProvider()
  const storage = useStorage("session", StorageData.crmAccount)
  const decoded = decodeToken<Token>(token)
  const accountNumber =
    decoded?.companyId === CrmAccount.Internal
      ? storage.get() || ""
      : decoded?.companyId || ""

  const [selectedMember, setSelectedMember] = useState<AccountTeam | null>(null)
  const [showContactModal, setShowContactModal] = useState<boolean>(false)

  const {getAccountTeamByAccountNumber, getAccountByAccountNumber} =
    accountEndpoints()

  const getAccountTeamQuery = useQuery<AccountTeam[]>({
    queryKey: ["accountTeam", accountNumber],
    queryFn: () => getAccountTeamByAccountNumber(accountNumber),
    enabled: !!accountNumber && accountNumber !== CrmAccount.Internal,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const getAccountQuery = useQuery<Account>({
    queryKey: ["account", accountNumber],
    queryFn: () => getAccountByAccountNumber(accountNumber),
    enabled: !!accountNumber && accountNumber !== CrmAccount.Internal,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const handleContactModalToggle = (member: AccountTeam) => {
    setSelectedMember(member)
    setShowContactModal(true)
  }

  const handleRefreshData = () => {
    getAccountTeamQuery.refetch()
    getAccountQuery.refetch()
  }

  const handleAskAnExpert = () => {
    const event = new CustomEvent("ask-an-expert", {
      detail: {
        show: true,
        relatesTo: "General",
      },
    })
    window.dispatchEvent(event)
  }

  return (
    <Fragment>
      <Stack
        id="account-team"
        component="article"
        width="100%"
        direction="column"
        gap="1.5rem"
      >
        {(getAccountTeamQuery.isError ||
          getAccountTeamQuery.isRefetchError ||
          getAccountTeamQuery.isLoadingError ||
          getAccountQuery.isError ||
          getAccountQuery.isRefetchError ||
          getAccountQuery.isLoadingError) &&
        !(
          getAccountTeamQuery.isLoading ||
          getAccountTeamQuery.isFetching ||
          getAccountTeamQuery.isRefetching ||
          getAccountQuery.isLoading ||
          getAccountQuery.isFetching ||
          getAccountQuery.isRefetching
        ) ? (
          <Stack
            aria-label="Unexpected error"
            direction="column"
            gap="40px"
            paddingTop="51px"
            marginBottom="-14px"
          >
            <Stack paddingX="8px">
              <Typography
                component="h3"
                fontSize="0.875rem"
                lineHeight="1.43"
                textAlign="center"
                color="#595958"
                fontWeight="400"
              >
                <Trans
                  i18nKey="We're sorry, we're having trouble retrieving this right now. Please try refreshing your browser or ask an expert."
                  defaults="We're sorry, we're having trouble retrieving this right now. Please try refreshing your browser or <AskAnExpertLink>ask an expert</AskAnExpertLink>."
                  components={{
                    AskAnExpertLink: (
                      <Typography
                        component="span"
                        color="#226CA5"
                        fontSize="0.875rem"
                        sx={{textDecoration: "underline", cursor: "pointer"}}
                        onClick={handleAskAnExpert}
                      >
                        ask an expert
                      </Typography>
                    ),
                  }}
                />
              </Typography>
            </Stack>
            <NewButton
              variant="outline"
              color="primary"
              text={t("Refresh")}
              fullWidth
              onClick={handleRefreshData}
              sx={{
                height: "32px",
              }}
            />
          </Stack>
        ) : (
          <Stack component="section" direction="column" gap="0.5rem">
            <Stack component="header" paddingX="0.25rem" paddingY="0.5rem">
              <Typography
                component="h3"
                fontSize="0.875rem"
                fontWeight="600"
                fontFamily="Inter, sans-serif"
                lineHeight="1.43"
                letterSpacing="normal"
                textAlign="left"
                color="#1c1c1c"
              >
                {t("Account Team")}
              </Typography>
            </Stack>
            {!accountNumber ||
            getAccountTeamQuery.isLoading ||
            getAccountTeamQuery.isFetching ||
            getAccountQuery.isLoading ||
            getAccountQuery.isFetching ? (
              [1, 2, 3].map((index) => (
                <AccountCard key={index} name="" loading={true} />
              ))
            ) : accountNumber === CrmAccount.Internal ? (
              <AccountCard
                name="Nick Allan"
                id="nick.allan@controlrisks.com"
                onClick={() =>
                  handleContactModalToggle({
                    accountTeamEmailAddress: "MyControlRisks@controlrisks.com",
                    accountTeamFullName: "Nick Allan",
                    accountTeamId: "",
                    crmAccountId: "",
                    crmAccountName: "Control Risks",
                    crmAccountNumber: accountNumber,
                  })
                }
              />
            ) : (
              <>
                {getAccountQuery.data?.relationshipManager ? (
                  <AccountCard
                    name={getAccountQuery.data.relationshipManager}
                    id={getAccountQuery.data.relationshipManagerId}
                    onClick={() =>
                      handleContactModalToggle({
                        accountTeamEmailAddress:
                          getAccountQuery.data.relationshipManagerId!,
                        accountTeamFullName:
                          getAccountQuery.data.relationshipManager,
                        accountTeamId: "",
                        crmAccountId: getAccountQuery.data.crmAccountId,
                        crmAccountName: getAccountQuery.data.crmAccountName,
                        crmAccountNumber: accountNumber,
                      })
                    }
                  />
                ) : null}
                {cardCount <= 0
                  ? null
                  : cardCount < (getAccountTeamQuery.data?.length || 0)
                  ? getAccountTeamQuery.data
                      ?.sort((a, b) => {
                        if (a.accountTeamFullName < b.accountTeamFullName) {
                          return -1
                        }
                        if (a.accountTeamFullName > b.accountTeamFullName) {
                          return 1
                        }
                        return 0
                      })
                      ?.slice(0, cardCount)
                      .map((team) => (
                        <AccountCard
                          key={team.accountTeamId}
                          name={team.accountTeamFullName}
                          id={team.accountTeamId}
                          onClick={() => handleContactModalToggle(team)}
                        />
                      ))
                  : getAccountTeamQuery.data
                      ?.sort((a, b) => {
                        if (a.accountTeamFullName < b.accountTeamFullName) {
                          return -1
                        }
                        if (a.accountTeamFullName > b.accountTeamFullName) {
                          return 1
                        }
                        return 0
                      })
                      ?.map((team) => (
                        <AccountCard
                          key={team.accountTeamId}
                          name={team.accountTeamFullName}
                          id={team.accountTeamId}
                          onClick={() => handleContactModalToggle(team)}
                        />
                      ))}
              </>
            )}
            {!(getAccountQuery.isLoading || getAccountQuery.isFetching) ? (
              <Stack
                direction="row"
                width="fit-content"
                marginLeft="auto"
                justifyContent="flex-end"
                gap="0.25rem"
                sx={{cursor: "pointer"}}
                onClick={() => navigate("/profile")}
              >
                <Stack
                  height="22px"
                  alignItems="center"
                  justifyContent="center"
                  borderBottom="1px solid #053747"
                >
                  <Typography
                    component="span"
                    fontSize="0.75rem"
                    fontWeight="600"
                    fontStyle="normal"
                    lineHeight="1.43"
                    letterSpacing="normal"
                    textAlign="center"
                    color="#053747"
                  >
                    {t("View more")}
                  </Typography>
                </Stack>
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  height="20px"
                  width="20px"
                  sx={{
                    div: {
                      height: "14px",
                    },
                    svg: {
                      height: "14px",
                      path: {
                        fill: "#053747",
                      },
                    },
                  }}
                >
                  <ReactSVG src="/icons/chevron-right.svg" />
                </Stack>
              </Stack>
            ) : null}
          </Stack>
        )}
        <Stack component="footer">
          <Button
            sx={{
              color: "#fff",
              backgroundColor: "#053747",
              width: "177px",
              minHeight: "40px",
              borderRadius: "4px",
              fontSize: "16px",
              fontWeight: "600",
            }}
            variant="contained"
            onClick={() => setShowFindExpert(true)}
          >
            {t("Find an expert")}
          </Button>
          <FindExpert />
        </Stack>
      </Stack>
      <ContactModal
        show={showContactModal}
        handleToggle={() => setShowContactModal((s) => !s)}
        member={selectedMember!!}
        account={getAccountQuery.data!!}
        accountTeam={getAccountTeamQuery.data!!}
      />
    </Fragment>
  )
}

AccountPanel.displayName = "AccountPanel"

export default AccountPanel
